import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertTriangle,
  Clock,
  CreditCard,
  Calendar,
  Euro,
  ChevronRight,
  X,
  Bell,
  FileText
} from 'lucide-react';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// Status configuration
const STATUS_CONFIG = {
  overdue: { 
    label: 'En retard', 
    color: 'bg-red-100 text-red-700 border-red-200',
    icon: AlertTriangle,
    urgent: true
  },
  to_pay: { 
    label: 'À payer', 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: CreditCard,
    urgent: false
  },
  pending_invoice: { 
    label: 'Attente facture', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: FileText,
    urgent: false
  },
  upcoming: { 
    label: 'À venir', 
    color: 'bg-slate-100 text-slate-700 border-slate-200',
    icon: Calendar,
    urgent: false
  },
  paid: { 
    label: 'Payé', 
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CreditCard,
    urgent: false
  }
};

// Service type labels
const SERVICE_TYPE_LABELS = {
  domain: 'Nom de domaine',
  hosting: 'Hébergement',
  contract: 'Contrat',
  subscription: 'Abonnement',
  other: 'Autre service'
};

const PaymentAlertsWidget = ({ showPopupOnLoad = false }) => {
  const { user, isClient } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);

  useEffect(() => {
    if (isClient) {
      fetchSummary();
    }
  }, [isClient]);

  // Show popup on load if there are urgent payments
  useEffect(() => {
    if (showPopupOnLoad && summary?.has_urgent && !popupDismissed) {
      // Check if user has already seen this popup today
      const lastDismissed = localStorage.getItem('payment_popup_dismissed');
      const today = new Date().toDateString();
      if (lastDismissed !== today) {
        setShowPopup(true);
      }
    }
  }, [summary, showPopupOnLoad, popupDismissed]);

  const fetchSummary = async () => {
    try {
      const res = await api.get('/services/client-summary');
      setSummary(res.data);
    } catch (error) {
      console.error('Error fetching payment summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const dismissPopup = () => {
    setShowPopup(false);
    setPopupDismissed(true);
    localStorage.setItem('payment_popup_dismissed', new Date().toDateString());
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (!isClient || loading) return null;

  // Don't show if no pending payments
  const hasPayments = summary && (summary.overdue?.length > 0 || summary.to_pay?.length > 0 || summary.upcoming?.length > 0);
  if (!hasPayments) return null;

  return (
    <>
      {/* Widget Card */}
      <Card className={cn(
        "border-2",
        summary?.has_urgent ? "border-red-300 bg-red-50" : "border-orange-200 bg-orange-50"
      )} data-testid="payment-alerts-widget">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            {summary?.has_urgent ? (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            ) : (
              <CreditCard className="w-5 h-5 text-orange-500" />
            )}
            Règlements en attente
            {summary?.has_urgent && (
              <Badge className="bg-red-500 text-white text-xs">Urgent</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {/* Overdue */}
            {summary?.overdue?.length > 0 && (
              <div className="flex items-center justify-between p-2 bg-red-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    {summary.overdue.length} en retard
                  </span>
                </div>
                <span className="font-bold text-red-700">
                  {formatAmount(summary.total_overdue)}
                </span>
              </div>
            )}

            {/* To Pay */}
            {summary?.to_pay?.length > 0 && (
              <div className="flex items-center justify-between p-2 bg-orange-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-800">
                    {summary.to_pay.length} à payer
                  </span>
                </div>
                <span className="font-bold text-orange-700">
                  {formatAmount(summary.total_to_pay)}
                </span>
              </div>
            )}

            {/* Upcoming */}
            {summary?.upcoming?.length > 0 && (
              <div className="flex items-center justify-between p-2 bg-slate-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-600" />
                  <span className="text-sm text-slate-700">
                    {summary.upcoming.length} à venir
                  </span>
                </div>
              </div>
            )}

            {/* Action Button */}
            <Button
              className="w-full mt-2"
              variant={summary?.has_urgent ? "destructive" : "default"}
              onClick={() => navigate('/invoices?tab=payments')}
            >
              Voir tous les règlements
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Popup Alert Dialog */}
      <Dialog open={showPopup} onOpenChange={setShowPopup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Règlements en attente !
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-slate-600 mb-4">
              Vous avez des règlements en attente qui nécessitent votre attention :
            </p>

            <div className="space-y-3">
              {summary?.overdue?.map((service) => (
                <div key={service.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-red-800">{service.name}</p>
                      <p className="text-xs text-red-600">
                        {SERVICE_TYPE_LABELS[service.service_type] || service.service_type}
                        {service.due_date && ` • Échéance: ${formatDate(service.due_date)}`}
                      </p>
                    </div>
                    <span className="font-bold text-red-700">
                      {formatAmount(service.amount)}
                    </span>
                  </div>
                </div>
              ))}

              {summary?.to_pay?.slice(0, 3).map((service) => (
                <div key={service.id} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-orange-800">{service.name}</p>
                      <p className="text-xs text-orange-600">
                        {SERVICE_TYPE_LABELS[service.service_type] || service.service_type}
                        {service.due_date && ` • Échéance: ${formatDate(service.due_date)}`}
                      </p>
                    </div>
                    <span className="font-bold text-orange-700">
                      {formatAmount(service.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {(summary?.to_pay?.length > 3) && (
              <p className="text-sm text-slate-500 mt-2">
                Et {summary.to_pay.length - 3} autre(s) règlement(s)...
              </p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={dismissPopup}>
              Plus tard
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                dismissPopup();
                navigate('/invoices?tab=payments');
              }}
            >
              Voir les règlements
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentAlertsWidget;
